import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TETRIS_CREDIT_REWARD } from '../types/subscription';

const GAME_WIDTH = 300;
const GAME_HEIGHT = 500;
const LANES = 3;
const LANE_WIDTH = GAME_WIDTH / LANES;
const CAR_WIDTH = 50;
const CAR_HEIGHT = 80;
const OBSTACLE_WIDTH = 50;
const OBSTACLE_HEIGHT = 80;
const TARGET_SCORE = 500;

interface Obstacle {
  id: number;
  lane: number;
  y: number;
  type: 'car' | 'truck';
  color: string;
}

interface RacingGameProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (score: number) => void;
}

const CAR_COLORS = ['#e74c3c', '#3498db', '#9b59b6', '#f39c12', '#1abc9c'];
const PLAYER_COLOR = '#2ecc71';

const RacingGame: React.FC<RacingGameProps> = ({ isOpen, onClose, onComplete }) => {
  const [playerLane, setPlayerLane] = useState(1);
  const [obstacles, setObstacles] = useState<Obstacle[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [speed, setSpeed] = useState(2);
  const gameLoopRef = useRef<number | null>(null);
  const obstacleIdRef = useRef(0);
  const lastObstacleTimeRef = useRef(0);
  const touchStartRef = useRef<number | null>(null);

  // Spawn obstacle
  const spawnObstacle = useCallback(() => {
    const lane = Math.floor(Math.random() * LANES);
    const color = CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)];
    const type = Math.random() > 0.7 ? 'truck' : 'car';

    setObstacles(prev => [
      ...prev,
      {
        id: obstacleIdRef.current++,
        lane,
        y: -OBSTACLE_HEIGHT,
        type,
        color,
      },
    ]);
  }, []);

  // Game loop
  const gameLoop = useCallback(() => {
    if (gameOver || !gameStarted) return;

    const now = Date.now();

    // Spawn obstacles
    const spawnInterval = Math.max(800 - score, 400);
    if (now - lastObstacleTimeRef.current > spawnInterval) {
      spawnObstacle();
      lastObstacleTimeRef.current = now;
    }

    // Move obstacles and check collisions
    setObstacles(prev => {
      const newObstacles = prev
        .map(obs => ({ ...obs, y: obs.y + speed }))
        .filter(obs => obs.y < GAME_HEIGHT + OBSTACLE_HEIGHT);

      // Check collision
      for (const obs of newObstacles) {
        if (obs.lane === playerLane) {
          const playerY = GAME_HEIGHT - CAR_HEIGHT - 20;
          const obsBottom = obs.y + OBSTACLE_HEIGHT;
          const playerTop = playerY;
          const playerBottom = playerY + CAR_HEIGHT;
          const obsTop = obs.y;

          if (obsBottom > playerTop && obsTop < playerBottom) {
            setGameOver(true);
            return prev;
          }
        }
      }

      return newObstacles;
    });

    // Update score
    setScore(prev => {
      const newScore = prev + 1;
      // Increase speed every 100 points (max speed 6)
      if (newScore % 100 === 0 && speed < 6) {
        setSpeed(s => s + 0.5);
      }
      return newScore;
    });
  }, [gameOver, gameStarted, playerLane, speed, score, spawnObstacle]);

  // Start game loop
  useEffect(() => {
    if (gameStarted && !gameOver) {
      const loop = () => {
        gameLoop();
        gameLoopRef.current = requestAnimationFrame(loop);
      };
      gameLoopRef.current = requestAnimationFrame(loop);
    }

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, gameLoop]);

  // Check win condition
  useEffect(() => {
    if (score >= TARGET_SCORE && !gameOver) {
      setGameOver(true);
      onComplete(score);
    }
  }, [score, gameOver, onComplete]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted || gameOver) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setPlayerLane(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setPlayerLane(prev => Math.min(LANES - 1, prev + 1));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStarted, gameOver]);

  // Touch controls
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartRef.current === null || !gameStarted || gameOver) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchEnd - touchStartRef.current;

    if (Math.abs(diff) > 30) {
      if (diff > 0) {
        setPlayerLane(prev => Math.min(LANES - 1, prev + 1));
      } else {
        setPlayerLane(prev => Math.max(0, prev - 1));
      }
    }

    touchStartRef.current = null;
  };

  // Tap to move
  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!gameStarted || gameOver) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const tapLane = Math.floor(x / LANE_WIDTH);

    if (tapLane < playerLane) {
      setPlayerLane(prev => Math.max(0, prev - 1));
    } else if (tapLane > playerLane) {
      setPlayerLane(prev => Math.min(LANES - 1, prev + 1));
    }
  };

  // Start game
  const startGame = () => {
    setPlayerLane(1);
    setObstacles([]);
    setScore(0);
    setSpeed(2);
    setGameOver(false);
    setGameStarted(true);
    lastObstacleTimeRef.current = Date.now();
    obstacleIdRef.current = 0;
  };

  // Handle close
  const handleClose = () => {
    setGameStarted(false);
    setObstacles([]);
    onClose();
  };

  if (!isOpen) return null;

  const playerX = playerLane * LANE_WIDTH + (LANE_WIDTH - CAR_WIDTH) / 2;
  const playerY = GAME_HEIGHT - CAR_HEIGHT - 20;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4 max-h-[95vh] overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-700 dark:text-gray-200">Racing Challenge</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Instructions or Game */}
        {!gameStarted ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🏎️</div>
            <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-200 mb-4">
              Earn {TETRIS_CREDIT_REWARD} Extra Credits!
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Reach {TARGET_SCORE} points to earn bonus recording credits.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Swipe or tap to change lanes. Avoid the cars!
            </p>
            <button
              onClick={startGame}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-colors"
            >
              Start Racing!
            </button>
          </div>
        ) : (
          <>
            {/* Score */}
            <div className="flex justify-around mb-4">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Score</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{score}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Target</p>
                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{TARGET_SCORE}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Speed</p>
                <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{speed}x</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-100"
                  style={{ width: `${Math.min((score / TARGET_SCORE) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Game Board */}
            <div
              className="relative mx-auto rounded-lg overflow-hidden cursor-pointer select-none"
              style={{
                width: GAME_WIDTH,
                height: GAME_HEIGHT,
                background: 'linear-gradient(to bottom, #2d3436, #636e72)',
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onClick={handleTap}
            >
              {/* Road markings */}
              {[1, 2].map(i => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 w-1"
                  style={{
                    left: i * LANE_WIDTH - 2,
                    background: 'repeating-linear-gradient(to bottom, #f1c40f 0, #f1c40f 30px, transparent 30px, transparent 60px)',
                    animation: 'roadMove 0.5s linear infinite',
                  }}
                />
              ))}

              {/* Side lines */}
              <div className="absolute top-0 bottom-0 left-1 w-1 bg-white" />
              <div className="absolute top-0 bottom-0 right-1 w-1 bg-white" />

              {/* Obstacles */}
              {obstacles.map(obs => (
                <div
                  key={obs.id}
                  className="absolute transition-transform duration-75"
                  style={{
                    left: obs.lane * LANE_WIDTH + (LANE_WIDTH - OBSTACLE_WIDTH) / 2,
                    top: obs.y,
                    width: OBSTACLE_WIDTH,
                    height: OBSTACLE_HEIGHT,
                  }}
                >
                  {/* Car body */}
                  <div
                    className="w-full h-full rounded-lg relative"
                    style={{ backgroundColor: obs.color }}
                  >
                    {/* Windshield */}
                    <div
                      className="absolute rounded"
                      style={{
                        top: '15%',
                        left: '15%',
                        right: '15%',
                        height: '25%',
                        backgroundColor: 'rgba(255,255,255,0.3)',
                      }}
                    />
                    {/* Wheels */}
                    <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ top: '10%', left: '-4px' }} />
                    <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ top: '10%', right: '-4px' }} />
                    <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ bottom: '10%', left: '-4px' }} />
                    <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ bottom: '10%', right: '-4px' }} />
                  </div>
                </div>
              ))}

              {/* Player car */}
              <div
                className="absolute transition-all duration-100"
                style={{
                  left: playerX,
                  top: playerY,
                  width: CAR_WIDTH,
                  height: CAR_HEIGHT,
                }}
              >
                {/* Car body */}
                <div
                  className="w-full h-full rounded-lg relative shadow-lg"
                  style={{ backgroundColor: PLAYER_COLOR }}
                >
                  {/* Windshield */}
                  <div
                    className="absolute rounded"
                    style={{
                      bottom: '15%',
                      left: '15%',
                      right: '15%',
                      height: '25%',
                      backgroundColor: 'rgba(255,255,255,0.4)',
                    }}
                  />
                  {/* Racing stripe */}
                  <div
                    className="absolute bg-white"
                    style={{
                      top: '5%',
                      bottom: '5%',
                      left: '45%',
                      width: '10%',
                      borderRadius: '2px',
                    }}
                  />
                  {/* Wheels */}
                  <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ top: '10%', left: '-4px' }} />
                  <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ top: '10%', right: '-4px' }} />
                  <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ bottom: '10%', left: '-4px' }} />
                  <div className="absolute w-2 h-4 bg-gray-900 rounded-sm" style={{ bottom: '10%', right: '-4px' }} />
                </div>
              </div>

              {/* Game Over Overlay */}
              {gameOver && (
                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                  <p className="text-3xl font-bold text-white mb-2">
                    {score >= TARGET_SCORE ? '🏆 You Won!' : '💥 Crash!'}
                  </p>
                  <p className="text-xl text-gray-300 mb-2">Score: {score}</p>
                  {score >= TARGET_SCORE && (
                    <p className="text-xl text-emerald-400 font-bold mb-4">
                      +{TETRIS_CREDIT_REWARD} Credits Earned!
                    </p>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={startGame}
                      className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors"
                    >
                      Play Again
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Controls */}
            {!gameOver && (
              <div className="mt-4 flex justify-center gap-4">
                <button
                  onClick={() => setPlayerLane(prev => Math.max(0, prev - 1))}
                  className="w-20 h-14 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center text-2xl font-bold text-gray-700 dark:text-gray-200 active:bg-gray-300 dark:active:bg-gray-600"
                >
                  ◀
                </button>
                <button
                  onClick={() => setPlayerLane(prev => Math.min(LANES - 1, prev + 1))}
                  className="w-20 h-14 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center text-2xl font-bold text-gray-700 dark:text-gray-200 active:bg-gray-300 dark:active:bg-gray-600"
                >
                  ▶
                </button>
              </div>
            )}

            {/* Desktop Instructions */}
            {!gameOver && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4 hidden md:block">
                Use ← → arrow keys to change lanes
              </p>
            )}
          </>
        )}
      </div>

      {/* CSS Animation for road */}
      <style>{`
        @keyframes roadMove {
          from { transform: translateY(0); }
          to { transform: translateY(60px); }
        }
      `}</style>
    </div>
  );
};

export default RacingGame;
